import { getProviders } from "next-auth/client";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import Login from "@/components/Layout/Login";
import { useSettings } from "@/components/SettingsContext";
import Signin from "@/components/Signin";
import { useUser } from "@/hooks/useUser";
import { GetServerSideProps } from "next";
import { TFunction } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const SigninPage = ({ providers }): React.ReactElement => {
  const { settings, patchSettings } = useSettings();
  const [session, loading, error] = useUser();
  const router = useRouter();

  const {
    token,
    from,
    id,
    name,
    representative,
    url,
    officeEmail,
    phone,
    address,
    city,
    postalCode,
    country,
    type,
  } = router?.query;

  useEffect(() => {
    if (!session?.user?.id || loading || error) return;

    const menuCollapsed = session?.user?.profile?.menuCollapsed;
    if (menuCollapsed && settings?.menuCollapsed === null)
      patchSettings({ menuCollapsed });

    const locale = session.user.profile?.locale;
    if (router?.query?.from)
      router.push(`${router.query.from}`, `${router.query.from}`, { locale });
    else if (token)
      router.push(`/invitation/${token}`, `/invitation/${token}`, { locale });
    else router.push("/", "/", { locale });
  }, [session, loading, error, token, from]);

  return (
    <Signin
      providers={providers}
      token={token}
      office={{
        id: +id,
        name: name?.toString(),
        representative: representative?.toString(),
        url: url?.toString(),
        officeEmail: officeEmail?.toString(),
        phone: +phone,
        address: address?.toString(),
        city: city?.toString(),
        postalCode: postalCode?.toString(),
        country: country?.toString(),
        type: type?.toString(),
      }}
    />
  );
};

SigninPage.getLayout = function getLayout(
  page: React.ReactElement,
  t: TFunction
) {
  return (
    <Login title={t("users.signinNoun")}>
      <Head>
        <title>{t("users.signinNoun")} - Accred.eu</title>
      </Head>
      {page}
    </Login>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const providers = await getProviders();

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale, ["common"])),
      providers,
    },
  };
};

export default SigninPage;
